import { useState } from 'react';
import type { AuthClient } from '../api/auth-client';
import { AuthCard } from './SignInPage';
import { authFailure, type AuthFailure } from './auth-errors';
import { FormMessage } from '../design-system/FormMessage';
import { PasswordInput, SubmitButton } from '../design-system/fields';

/**
 * Premier mot de passe ou réinitialisation (ADR-091 §5) : le lien porte un jeton à usage unique
 * dans le fragment de l'URL (`#/password/setup?token=…`), que le serveur ne voit qu'au moment de la
 * confirmation. Politique : 12 caractères au moins, une phrase de passe convient, aucune règle de
 * composition.
 */

const LINK = /^#\/password\/(setup|reset)\?(?:.*&)?token=([0-9a-f]{24}\.[A-Za-z0-9_-]{43})/;

export function passwordLinkOf(hash: string): { purpose: 'setup' | 'reset'; token: string } | null {
  const match = LINK.exec(hash);
  return match === null ? null : { purpose: match[1] as 'setup' | 'reset', token: match[2]! };
}

export function PasswordResetPage({ client, purpose, token, onDone }: { client: AuthClient; purpose: 'setup' | 'reset'; token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<AuthFailure | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (done) {
    return (
      <AuthCard title="Mot de passe enregistré">
        <div className="ay-stack ay-stack--column">
          <p role="status">Votre mot de passe est enregistré. Vos autres sessions ont été fermées.</p>
          <button type="button" className="ay-button" onClick={onDone}>
            Se connecter
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={purpose === 'setup' ? 'Choisissez votre mot de passe' : 'Nouveau mot de passe'}>
      <form
        className="ay-form ay-form--touch"
        onSubmit={(event) => {
          event.preventDefault();
          if (password !== confirmation) {
            setError({ text: 'Les deux saisies diffèrent.' });
            return;
          }
          setBusy(true);
          setError(null);
          client
            .confirmReset(token, password)
            .then(() => {
              setPassword('');
              setConfirmation('');
              setDone(true);
            })
            .catch((caught: unknown) => setError(authFailure(caught)))
            .finally(() => setBusy(false));
        }}
      >
        <p className="ay-text--muted">Au moins 12 caractères. Une phrase de plusieurs mots est plus sûre et plus facile à retenir qu’un mot compliqué.</p>
        <label className="ay-field">
          <span className="ay-field__label">Nouveau mot de passe</span>
          <PasswordInput name="new-password" autoComplete="new-password" minLength={12} required value={password} onValueChange={setPassword} />
        </label>
        <label className="ay-field">
          <span className="ay-field__label">Confirmation</span>
          <PasswordInput name="confirm-password" autoComplete="new-password" required value={confirmation} onValueChange={setConfirmation} />
        </label>
        <div className="ay-form__submit">
          <SubmitButton busy={busy} busyLabel="Enregistrement…" className="ay-button--strong">
            Enregistrer
          </SubmitButton>
        </div>
        <FormMessage message={error?.text ?? null} detail={error?.detail} />
      </form>
    </AuthCard>
  );
}
